import chroma from "chroma-js";

export const useColor = () => {
  const isLightColor = (color: string): boolean => {
    const brightness = chroma(color).luminance();

    return brightness > 0.5;
  };

  const isGradient = (color: string | undefined): boolean => {
    return color?.includes("gradient") ?? false;
  };

  const isTransparent = (color: string): boolean => {
    if (!color) return false;

    return chroma(color).alpha() === 0;
  };

  /**
   * 주어진 색상을 밝게 만들어 반환
   *
   * @param color 색상
   * @param lightness 밝기 (기본값 0.93)
   * @param saturation 채도 (기본값 0.15)
   */
  const getLightnessColor = (color: string, lightness: number = 0.93, saturation: number = 0.15): string => {
    try {
      // 입력된 색상을 chroma 객체로 변환
      const baseColor = chroma(color);

      // 현재 색상의 HSL 값을 가져옴
      const [hue, baseSaturation] = baseColor.hsl();

      // - hue: 원래 색상의 색조를 유지
      // - saturation: 원래 채도의 몇%만 사용하여 옅게 만듦
      // - lightness: 기본값 0.95 (매우 밝게)
      return chroma.hsl(hue, baseSaturation * saturation, lightness).hex();
    } catch (error) {
      console.error("Invalid color provided:", error);
      return "#fafafa"; // 오류 발생시 흰색 반환
    }
  };

  const getComplementaryColor = (color: string, tone: number = 0.5): string => {
    if (tone < 0 || tone > 1) {
      throw new Error("Tone must be between 0 and 1");
    }

    const hslColor = chroma(color).hsl();
    const complementaryHue = (hslColor[0] + 180) % 360;
    const complementaryColor = chroma.hsl(complementaryHue, hslColor[1], 1 - tone);

    return complementaryColor.hex();
  };

  const getFirstColorOfGradient = (gradient: string): string | null => {
    const colorRegex = /rgba?\([^)]+\)/i;
    const match = gradient.match(colorRegex);

    if (match) {
      return match[0];
    }

    return null;
  };

  const getLastColorOfGradient = (gradient: string): string | null => {
    const colorRegex = /rgba?\([^)]+\)/gi;
    const matches = gradient.match(colorRegex);

    if (matches) {
      return matches[matches.length - 1];
    }

    return null;
  };

  const getAppropriateTextColor = (value: string): string => {
    const color = chroma(value); // Ensure color is a chroma object
    const luminance = color.luminance(); // Get luminance value
    const saturation = color.get("hsl.s"); // Get saturation from HSL format

    // Default colors for text
    const darkText = "#000000";
    const lightText = "#FFFFFF";

    // If color is transparent, return dark text
    if (color.alpha() === 0) return darkText;

    // Evaluate lightness and adjust text color accordingly
    if (luminance > 0.5) {
      // Light background, use dark text
      return saturation < 0.1 ? chroma(darkText).darken(2).hex() : darkText;
    } else {
      // Dark background, use light text
      return saturation < 0.1 ? chroma(lightText).brighten(2).hex() : lightText;
    }
  };

  const getImageLuminance = async (url: string): Promise<number> => {
    return new Promise<number>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = function () {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Unable to get 2D context"));
          return;
        }
        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let totalLuminance = 0;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          // chroma.js를 이용해 밝기 계산
          const luminance = chroma(r, g, b).luminance();
          totalLuminance += luminance;
        }

        const averageLuminance = totalLuminance / (canvas.width * canvas.height);

        resolve(averageLuminance);
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = url;
    });
  };

  return {
    isLightColor,
    isGradient,
    isTransparent,
    getAppropriateTextColor,
    getComplementaryColor,
    getImageLuminance,
    getFirstColorOfGradient,
    getLastColorOfGradient,
    getLightnessColor,
  };
};
